import React, { useState, useContext } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as Progress from '@radix-ui/react-progress';
import { auth, functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { PatientDataContext } from '../../../../context/PatientDataContext';
import { serverTimestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/solid';

export default function PatientReportWizard({ open, onOpenChange, patientId, clinicianId, reportType }) {    

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showProgressDialog, setShowProgressDialog] = useState(false);
    const [progressValues, setProgressValues] = useState([0, 0, 0]);
    const [currentStep, setCurrentStep] = useState(0);    
    const [downloadError, setDownloadError] = useState(null);
    const { updatePatientFirestore } = useContext(PatientDataContext);
    const navigate = useNavigate();

    const handleGenerateSection = async () => {
        try {
            setIsSubmitting(true);
            
            // Prepare data for cloud function
            const data = {
                patientId,
                clinicianId,  
                reportType,
            };    

            console.log('Sending data to cloud function:', data);

            // Show progress dialog and reset progress state
            setProgressValues([0, 0, 0]);
            setCurrentStep(0);
            setShowProgressDialog(true);

            // Call the cloud function (to be implemented)
            const generatePatientReport = httpsCallable(functions, 'generatePatientReport');

            // Simulate progress while the real function executes
            const simulateProgress = async () => {
                // First step progress - Processing data
                await new Promise(resolve => {
                  const timer1 = setTimeout(() => setProgressValues([15, 0, 0]), 1500);
                  const timer2 = setTimeout(() => setProgressValues([30, 0, 0]), 2500);
                  const timer3 = setTimeout(() => setProgressValues([45, 0, 0]), 5000);
                  const timer4 = setTimeout(() => setProgressValues([70, 0, 0]), 7500);
                  const timer5 = setTimeout(() => {
                    setProgressValues([100, 0, 0]);
                    setCurrentStep(1);
                    resolve();
                  }, 10000);
              
                  return () => {
                    clearTimeout(timer1);
                    clearTimeout(timer2);
                    clearTimeout(timer3);
                    clearTimeout(timer4);
                    clearTimeout(timer5);
                  };
                });
              
                // Second step progress - Analysing scale results
                await new Promise(resolve => {
                  const timer1 = setTimeout(() => setProgressValues([100, 20, 0]), 1000);
                  const timer2 = setTimeout(() => setProgressValues([100, 40, 0]), 2750);
                  const timer3 = setTimeout(() => setProgressValues([100, 60, 0]), 4500);
                  const timer4 = setTimeout(() => setProgressValues([100, 80, 0]), 7000);
                  const timer5 = setTimeout(() => {
                    setProgressValues([100, 100, 0]);
                    setCurrentStep(2);
                    resolve();
                  }, 10000);
              
                  return () => {
                    clearTimeout(timer1);
                    clearTimeout(timer2);
                    clearTimeout(timer3);
                    clearTimeout(timer4);
                    clearTimeout(timer5);
                  };
                });
              
                // Third step progress - Creating report section
                await new Promise(resolve => {
                  const timer1 = setTimeout(() => setProgressValues([100, 100, 15]), 1000);
                  const timer2 = setTimeout(() => setProgressValues([100, 100, 30]), 3500);
                  const timer3 = setTimeout(() => setProgressValues([100, 100, 50]), 4500);
                  const timer4 = setTimeout(() => setProgressValues([100, 100, 75]), 6500);
                  const timer5 = setTimeout(() => {
                    setProgressValues([100, 100, 100]);
                    resolve();
                  }, 10000);
              
                  return () => {
                    clearTimeout(timer1);
                    clearTimeout(timer2);
                    clearTimeout(timer3);
                    clearTimeout(timer4);
                    clearTimeout(timer5);
                  };
                });
              };

            // Start simulating progress
            simulateProgress();

            // Call the actual function
            const result = await generatePatientReport(data);
            
            console.log('Cloud function result:', result.data);

            await updatePatientFirestore(patientId, {
                '6_manageReports': {
                  prReportGenerated: true,
                  prReportGeneratedAt: serverTimestamp(),
                  prReportGeneratedBy: auth.currentUser.uid,
                  prReportGeneratedByName: auth.currentUser.displayName,                       
                }
              }); 

        } catch (error) {
            console.error('Error generating section:', error);
            setDownloadError(error.message || 'An unexpected error occurred');
            // Ensure progress dialog stays open to show the error
            setProgressValues([100, 100, 100]);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseDialog = () => {
        setShowProgressDialog(false);
        onOpenChange(false);
    };
    
    
    // Check if all progress steps are complete
    const isProgressComplete = progressValues.every(value => value === 100);

    return (
        <>
            <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
                <AlertDialog.Portal>
                    <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 z-50" />
                    <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[85vh] w-[90vw] max-w-[550px] bg-white rounded-xl shadow-lg p-6 z-50 overflow-hidden flex flex-col">
                        <AlertDialog.Title className="text-xl font-semibold text-gray-900 mb-2">
                            Generate Patient Report
                        </AlertDialog.Title>
                        <AlertDialog.Description className="text-gray-600">
                            You can now generate a version of the Assessment Report for the patient.
                        </AlertDialog.Description>

                        <div className="flex-grow overflow-y-auto p-0.5 mb-4" style={{ maxHeight: '50vh' }}>                            

                            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                                <p className="text-sm text-blue-800">
                                    This version will rewrite sections of the Assessment Report in accessible and neuro-affirmative language for the patient.
                                </p>
                            </div>
                        </div>

                        <div className="font-semibold flex justify-end gap-3 mt-auto pt-2 border-t">
                            <AlertDialog.Cancel className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
                                Cancel
                            </AlertDialog.Cancel>
                            <AlertDialog.Action
                                onClick={handleGenerateSection}
                                disabled={isSubmitting}
                                className={`px-4 py-2 bg-violet-600 text-white rounded-lg transition-colors ${
                                    isSubmitting
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'hover:bg-violet-700'
                                }`}
                            >
                                {isSubmitting ? 'Generating...' : 'Generate Patient Report'}
                            </AlertDialog.Action>
                        </div>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>

            {/* Progress Dialog */}
            <AlertDialog.Root open={showProgressDialog} onOpenChange={setShowProgressDialog}>
                <AlertDialog.Portal>
                    <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 z-50" />
                    <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 w-[400px] z-50">
                        <AlertDialog.Title className="text-lg font-semibold mb-4">
                            Generating Patient Report
                        </AlertDialog.Title>

                        <AlertDialog.Description className="text-sm text-gray-600 mb-4">
                            Please wait while the Patient Report is prepared.
                        </AlertDialog.Description>
                        
                        <div className="space-y-6">
                            {/* Step 1 */}
                            <div className="w-full">
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm">Processing Clinical Assessment Report...</span>
                                    {currentStep > 0 && <span className="text-green-500 text-xs">✔</span>}
                                </div>
                                <Progress.Root
                                    className="h-2 w-full bg-gray-200 rounded-full overflow-hidden"
                                    value={progressValues[0]}
                                >
                                    <Progress.Indicator
                                        className={`h-full rounded-full transition-transform duration-[300ms] ease-in-out ${
                                            progressValues[0] === 100 ? 'bg-green-600' : 'bg-violet-600'
                                        }`}
                                        style={{ transform: `translateX(-${100 - progressValues[0]}%)` }}
                                    />
                                </Progress.Root>
                            </div>

                            {/* Step 2 */}
                            <div className="w-full">
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm">Rewriting for Patient...</span>
                                    {currentStep > 1 && <span className="text-green-500 text-xs">✔</span>}
                                </div>
                                <Progress.Root
                                    className="h-2 w-full bg-gray-200 rounded-full overflow-hidden"
                                    value={progressValues[1]}
                                >
                                    <Progress.Indicator
                                        className={`h-full rounded-full transition-transform duration-[300ms] ease-in-out ${
                                            progressValues[1] === 100 ? 'bg-green-600' : 'bg-violet-600'
                                        }`}
                                        style={{ transform: `translateX(-${100 - progressValues[1]}%)` }}
                                    />
                                </Progress.Root>
                            </div>

                            {/* Step 3 */}
                            <div className="w-full">
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm">Verifying Health Literacy...</span>
                                    {isProgressComplete && <span className="text-green-500 text-xs">✔</span>}
                                </div>
                                <Progress.Root
                                    className="h-2 w-full bg-gray-200 rounded-full overflow-hidden"
                                    value={progressValues[2]}
                                >
                                    <Progress.Indicator
                                        className={`h-full rounded-full transition-transform duration-[300ms] ease-in-out ${
                                            progressValues[2] === 100 ? 'bg-green-600' : 'bg-violet-600'
                                        }`}
                                        style={{ transform: `translateX(-${100 - progressValues[2]}%)` }}
                                    />
                                </Progress.Root>
                            </div>

                            {/* Error message if download fails */}
                            {downloadError && (
                                <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded relative mt-4" role="alert">
                                    <strong className="font-bold">Error: </strong>
                                    <span className="block sm:inline">{downloadError}</span>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="font-semibold flex justify-between space-x-3 mt-8">
                                {!isProgressComplete ? (
                                    <button
                                        onClick={() => setShowProgressDialog(false)}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                    >
                                        Cancel
                                    </button>
                                ) : (
                                    <div className="flex justify-between w-full">
                                        <button
                                            onClick={handleCloseDialog}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                        >
                                            Close
                                        </button>
                                        <button
                                            onClick={() => navigate(`/patient/${patientId}/review-document-pr`)}
                                            className={`px-4 py-2 flex items-center text-sm font-medium text-white bg-violet-700 rounded-md transition-colors hover:bg-violet-800`}
                                        >
                                            <ChevronRightIcon className="w-4 h-4 mr-2" />
                                            Go to Review
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>
        </>
    );
}